<template>
  <div class="dmo" :class="{ 'dmo--dense': dense }">
    <div class="dmo-dow">
      <div v-for="l in dowLabels" :key="l" class="dmo-dow-cell">{{ l }}</div>
    </div>
    <div class="dmo-grid" :class="{ 'dmo-loading-grid': loading }">
      <div
        v-for="cell in cells"
        :key="cell.date"
        class="dmo-cell"
        :class="cellClass(cell)"
        @mousemove="onCellHover($event, cell)"
        @mouseleave="hideHover"
        @click="onCellClick(cell)"
      >
        <div class="dmo-num">{{ cell.dayNum }}</div>
        <div class="dmo-dots">
          <span
            v-for="doc in visibleDots(cell)"
            :key="doc.branchMemberId"
            class="dmo-dot"
            :style="dotStyle(doc)"
            @click.stop="onDotClick(cell, doc)"
          ></span>
          <span v-if="extraCount(cell) > 0" class="dmo-more"
            >+{{ extraCount(cell) }}</span
          >
        </div>
      </div>
    </div>
    <div v-if="loading" class="dmo-loading-text">กำลังโหลด...</div>

    <!-- custom popover: doctor colour dot + name, one per line -->
    <div
      v-if="hover.doctors.length"
      class="dmo-pop"
      :style="{ left: hover.x + 'px', top: hover.y + 'px' }"
    >
      <div
        v-for="d in hover.doctors"
        :key="d.branchMemberId"
        class="dmo-pop-row"
      >
        <span class="dmo-pop-dot" :style="{ background: d.color || '#bbb' }"></span>
        <span class="dmo-pop-name">{{ d.name }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { buildMonthGrid, MONTH_DOW_LABELS } from "@/utils/doctorMonthOverview";

// Presentational month-overview calendar shared by the QueueManage finder modal
// and the UserManage "จัดการแพทย์" tab. It never fetches: the host loads the
// batch schedule and passes `members` + a normalized `doctors` list in.
export default {
  name: "DoctorMonthOverview",
  props: {
    year: { type: Number, required: true },
    month: { type: Number, required: true }, // 1..12
    members: { type: Array, default: () => [] }, // batch resolver members[]
    // normalized doctors: [{ branchMemberId, name, color }]
    doctors: { type: Array, default: () => [] },
    // highlight a single doctor's working days (manage view: clicked list row)
    highlightDoctorId: { type: Number, default: null },
    // highlight days of any selected doctor (finder view: multiselect)
    highlightDoctorIds: { type: Array, default: () => [] },
    // highlight days of doctors whose name matches (optional text filter)
    searchText: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    maxDots: { type: Number, default: 10 },
    // the active/selected day, drawn with a ring (e.g. the sidebar's queue date)
    selectedDate: { type: String, default: null },
    // tighter cells/dots for narrow hosts (the QueueManage sidebar)
    dense: { type: Boolean, default: false },
    // when true, clicking a dot navigates to that day (like clicking the cell)
    // instead of emitting "doctor-click" (used by the QueueManage calendars).
    dotsSelectDay: { type: Boolean, default: false },
  },
  data() {
    return {
      hover: { x: 0, y: 0, doctors: [] },
    };
  },
  computed: {
    dowLabels() {
      return MONTH_DOW_LABELS;
    },
    cells() {
      return buildMonthGrid({
        year: this.year,
        month: this.month,
        members: this.members,
        doctors: this.doctors,
      }).cells;
    },
    highlightIdSet() {
      return new Set((this.highlightDoctorIds || []).map(Number));
    },
    filterActive() {
      return (
        this.highlightIdSet.size > 0 ||
        this.highlightDoctorId != null ||
        this.searchText.trim() !== ""
      );
    },
  },
  methods: {
    nameMatches(doc) {
      const q = this.searchText.trim();
      return q !== "" && (doc.name || "").indexOf(q) !== -1;
    },
    // Is this doctor the focus of the current highlight/selection/search?
    isDoctorActive(doc) {
      if (this.highlightIdSet.size > 0) {
        return this.highlightIdSet.has(Number(doc.branchMemberId));
      }
      if (this.highlightDoctorId != null) {
        return doc.branchMemberId === this.highlightDoctorId;
      }
      if (this.searchText.trim() !== "") return this.nameMatches(doc);
      return true;
    },
    cellMatched(cell) {
      return cell.doctors.some((d) => this.isDoctorActive(d));
    },
    cellClass(cell) {
      const cls = [];
      if (cell.outside) cls.push("is-outside");
      else cls.push("is-clickable"); // every in-month day navigates, even empty
      if (cell.doctors.length > 0) cls.push("is-work");
      if (this.filterActive && cell.doctors.length > 0) {
        cls.push(this.cellMatched(cell) ? "is-hl" : "is-dim");
      }
      if (!cell.outside && this.selectedDate && cell.date === this.selectedDate) {
        cls.push("is-selected");
      }
      return cls;
    },
    visibleDots(cell) {
      return cell.doctors.slice(0, this.maxDots);
    },
    extraCount(cell) {
      return Math.max(0, cell.doctors.length - this.maxDots);
    },
    dotStyle(doc) {
      const faded = this.filterActive && !this.isDoctorActive(doc);
      return {
        background: doc.color || "#bbb",
        opacity: faded ? 0.22 : 1,
      };
    },
    onCellHover(e, cell) {
      if (cell.outside || !cell.doctors.length) {
        this.hideHover();
        return;
      }
      // position near the cursor, clamped to the viewport (works inside modals
      // too since the popover is position:fixed)
      this.hover = {
        x: Math.min(e.clientX + 14, window.innerWidth - 210),
        y: e.clientY + 16,
        doctors: cell.doctors,
      };
    },
    hideHover() {
      if (this.hover.doctors.length) this.hover = { x: 0, y: 0, doctors: [] };
    },
    onCellClick(cell) {
      // adjacent-month padding isn't navigable; every in-month day is — including
      // days with no doctor, so staff can still jump there.
      if (cell.outside) return;
      this.$emit(
        "day-click",
        cell.date,
        cell.doctors.map((d) => d.branchMemberId)
      );
    },
    // A dot either navigates to the day (QueueManage calendars) or focuses the
    // doctor (DoctorManage overview), depending on `dotsSelectDay`.
    onDotClick(cell, doc) {
      if (this.dotsSelectDay) {
        this.onCellClick(cell);
        return;
      }
      this.$emit("doctor-click", doc.branchMemberId);
    },
  },
};
</script>

<style scoped>
.dmo-dow,
.dmo-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}
.dmo-dow {
  margin-bottom: 6px;
}
.dmo-dow-cell {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #999;
}
.dmo-cell {
  min-height: 60px;
  border: 1px solid #eef0f3;
  border-radius: 9px;
  background: #f7f8fa;
  padding: 5px;
  transition: border-color 0.12s, box-shadow 0.12s, opacity 0.12s;
}
.dmo-cell.is-work {
  background: #fff;
  border-color: #e3e6ec;
}
.dmo-cell.is-clickable {
  cursor: pointer;
}
.dmo-cell.is-clickable:hover {
  border-color: #4a7cff;
  box-shadow: 0 3px 12px rgba(74, 124, 255, 0.18);
}
.dmo-cell.is-outside {
  opacity: 0.45;
}
.dmo-cell.is-dim {
  opacity: 0.28;
}
.dmo-cell.is-hl {
  border-color: #1a8a4a;
  background: #f2fbf6;
}
.dmo-cell.is-selected {
  border-color: #4a7cff;
  box-shadow: 0 0 0 2px rgba(74, 124, 255, 0.35);
}
.dmo-num {
  font-size: 11px;
  color: #888;
  font-weight: 600;
}
.dmo-cell.is-work .dmo-num {
  color: #222;
}
.dmo-dots {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  margin-top: 5px;
}
.dmo-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  cursor: pointer;
}
.dmo-more {
  font-size: 9px;
  color: #888;
  font-weight: 600;
  align-self: center;
}

/* Dense variant for the narrow QueueManage sidebar: tighter cells + 7px dots.
   maxDots is supplied by the host (sidebar passes 5); the "+N" overflow stays
   inline on the same row via the wrapping .dmo-dots flexbox. */
.dmo--dense .dmo-dow,
.dmo--dense .dmo-grid {
  gap: 4px;
}
.dmo--dense .dmo-dow-cell {
  font-size: 10px;
}
.dmo--dense .dmo-cell {
  min-height: 42px;
  border-radius: 7px;
  padding: 3px 4px;
}
.dmo--dense .dmo-num {
  font-size: 10px;
}
.dmo--dense .dmo-dots {
  gap: 2px;
  margin-top: 3px;
}
.dmo--dense .dmo-dot {
  width: 7px;
  height: 7px;
}
.dmo--dense .dmo-more {
  font-size: 9px;
}
.dmo-loading-grid {
  opacity: 0.5;
  pointer-events: none;
}
.dmo-loading-text {
  text-align: center;
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}
.dmo-pop {
  position: fixed;
  z-index: 2000;
  background: #fff;
  border: 1px solid #e3e6ec;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  padding: 8px 10px;
  pointer-events: none;
  max-width: 220px;
}
.dmo-pop-row {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  line-height: 1.7;
  white-space: nowrap;
}
.dmo-pop-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dmo-pop-name {
  color: #333;
}
</style>
